import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Trophy, Users, Globe, ArrowRight, ChevronLeft, ChevronRight, Zap, Star, Target } from 'lucide-react'
import { Link } from 'react-router-dom'

// 11张真实小程序截图
const screenshots = [
  { src: '/images/ladder/Screenshot_2026-03-26-10-45-25-042_com.tencent.mm.jpg', label: '挑战榜首页' },
  { src: '/images/ladder/Screenshot_2026-03-26-10-45-28-294_com.tencent.mm.jpg', label: '排名列表' },
  { src: '/images/ladder/Screenshot_2026-03-26-10-45-35-704_com.tencent.mm.jpg', label: '选手详情' },
  { src: '/images/ladder/Screenshot_2026-03-26-10-45-49-203_com.tencent.mm.jpg', label: '比赛记录' },
  { src: '/images/ladder/Screenshot_2026-03-26-10-46-05-515_com.tencent.mm.jpg', label: '发起挑战' },
  { src: '/images/ladder/Screenshot_2026-03-26-10-46-14-657_com.tencent.mm.jpg', label: '赛果确认' },
  { src: '/images/ladder/Screenshot_2026-03-26-10-46-18-385_com.tencent.mm.jpg', label: '积分变化' },
  { src: '/images/ladder/Screenshot_2026-03-26-10-46-30-918_com.tencent.mm.jpg', label: '个人战绩' },
  { src: '/images/ladder/Screenshot_2026-03-26-10-46-50-078_com.tencent.mm.jpg', label: '城市榜单' },
  { src: '/images/ladder/Screenshot_2026-03-26-10-47-10-032_com.tencent.mm.jpg', label: '全球排名' },
]

const features = [
  {
    icon: Trophy,
    title: '挑战升降级',
    desc: '严格的升降级制度，积分实时更新，保持比赛的竞争活力',
    color: 'rgba(245,158,11,0.15)',
    textColor: '#fbbf24',
  },
  {
    icon: Users,
    title: '单打 / 双打',
    desc: '支持单打、双打多种比赛模式，灵活组合，随时开赛',
    color: 'rgba(139,92,246,0.15)',
    textColor: '#a78bfa',
  },
  {
    icon: Target,
    title: '实时积分榜',
    desc: '比赛结果即时录入，积分自动核算，排名动态更新',
    color: 'rgba(239,68,68,0.15)',
    textColor: '#f87171',
  },
  {
    icon: Globe,
    title: '6种语言',
    desc: '中英法德西日多语言支持，无障碍覆盖全球华人网球圈',
    color: 'rgba(34,197,94,0.15)',
    textColor: '#4ade80',
  },
  {
    icon: Zap,
    title: '一键发起挑战',
    desc: '扫码或搜索对手，一键发起挑战邀请，响应极速',
    color: 'rgba(59,130,246,0.15)',
    textColor: '#60a5fa',
  },
  {
    icon: Star,
    title: '战绩分析',
    desc: '完整比赛历史记录，胜率、积分趋势一目了然',
    color: 'rgba(236,72,153,0.15)',
    textColor: '#f472b6',
  },
]

export default function Ladder() {
  const [current, setCurrent] = useState(0)
  const [autoPlay, setAutoPlay] = useState(true)

  // 自动轮播
  useEffect(() => {
    if (!autoPlay) return
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % screenshots.length)
    }, 2800)
    return () => clearInterval(timer)
  }, [autoPlay])

  const prev = () => {
    setAutoPlay(false)
    setCurrent((c) => (c - 1 + screenshots.length) % screenshots.length)
  }
  const next = () => {
    setAutoPlay(false)
    setCurrent((c) => (c + 1) % screenshots.length)
  }

  return (
    <div className="min-h-screen pt-16">
      {/* ===== Hero ===== */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] opacity-15"
          style={{
            background: 'radial-gradient(ellipse, rgba(245,158,11,0.5), transparent 70%)',
            filter: 'blur(60px)',
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* 文案区 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-6"
              style={{
                background: 'rgba(245,158,11,0.15)',
                border: '1px solid rgba(245,158,11,0.3)',
                color: '#fbbf24',
              }}
            >
              <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
              网球挑战榜
            </div>

            <h1 className="text-4xl md:text-6xl font-black text-white mb-6 tracking-tight">
              Lafit 网球挑战榜
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              挑战升降级系统 · 实时积分排名 · 6 种语言全球覆盖
              <br />
              让每一场比赛都值得被记录
            </p>

            <Link
              to="/#download"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold transition-all duration-300 hover:gap-3"
              style={{
                background: '#f59e0b',
                boxShadow: '0 0 30px rgba(245,158,11,0.4)',
                color: '#000',
              }}
            >
              立即参与挑战
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>

          {/* ===== 截图轮播区 ===== */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="mt-20 flex flex-col items-center"
          >
            {/* 手机样机 + 图片 */}
            <div className="relative flex items-center justify-center gap-6">
              {/* 左侧 prev 截图 */}
              <div
                className="hidden md:block w-40 h-72 rounded-2xl overflow-hidden opacity-40 scale-90 cursor-pointer transition-all duration-300 hover:opacity-60"
                style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                onClick={prev}
              >
                <img
                  src={screenshots[(current - 1 + screenshots.length) % screenshots.length].src}
                  alt="prev"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* 中央主截图（手机样机效果） */}
              <div className="relative">
                {/* 手机外框 */}
                <div
                  className="absolute inset-0 rounded-[2.5rem] pointer-events-none z-10"
                  style={{
                    border: '3px solid rgba(255,255,255,0.15)',
                    boxShadow:
                      '0 0 0 1px rgba(0,0,0,0.5), 0 40px 80px rgba(0,0,0,0.6), 0 0 60px rgba(245,158,11,0.2)',
                  }}
                />
                {/* 刘海 */}
                <div
                  className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-5 rounded-full z-20"
                  style={{ background: 'rgba(0,0,0,0.8)' }}
                />

                {/* 截图内容 */}
                <div
                  className="w-64 h-[520px] rounded-[2.5rem] overflow-hidden"
                  style={{ background: '#0a0a0a' }}
                >
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={current}
                      src={screenshots[current].src}
                      alt={screenshots[current].label}
                      className="w-full h-full object-cover object-top"
                      initial={{ opacity: 0, scale: 1.04 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.97 }}
                      transition={{ duration: 0.35 }}
                    />
                  </AnimatePresence>
                </div>
              </div>

              {/* 右侧 next 截图 */}
              <div
                className="hidden md:block w-40 h-72 rounded-2xl overflow-hidden opacity-40 scale-90 cursor-pointer transition-all duration-300 hover:opacity-60"
                style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                onClick={next}
              >
                <img
                  src={screenshots[(current + 1) % screenshots.length].src}
                  alt="next"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* 标签 */}
            <div className="mt-5 text-sm font-medium" style={{ color: '#fbbf24' }}>
              {screenshots[current].label}
            </div>

            {/* 控制按钮 + 指示点 */}
            <div className="mt-4 flex items-center gap-4">
              <button
                onClick={prev}
                className="p-2 rounded-full transition-all hover:scale-110"
                style={{ background: 'rgba(255,255,255,0.08)', color: '#9ca3af' }}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <div className="flex gap-2">
                {screenshots.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => { setAutoPlay(false); setCurrent(i) }}
                    className="rounded-full transition-all duration-300"
                    style={{
                      width: i === current ? '20px' : '6px',
                      height: '6px',
                      background: i === current ? '#f59e0b' : 'rgba(255,255,255,0.2)',
                    }}
                  />
                ))}
              </div>

              <button
                onClick={next}
                className="p-2 rounded-full transition-all hover:scale-110"
                style={{ background: 'rgba(255,255,255,0.08)', color: '#9ca3af' }}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* 自动播放提示 */}
            <p className="mt-3 text-xs text-gray-600">
              {autoPlay ? '自动播放中 · 点击箭头手动切换' : '手动模式 · 自动播放已暂停'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== 特性网格 ===== */}
      <section className="py-24 relative">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">核心功能</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              专业的挑战赛系统，覆盖从发起挑战到赛果分析的完整闭环
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                viewport={{ once: true }}
                className="p-6 rounded-2xl group hover:scale-[1.02] transition-all duration-300"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: feature.color, color: feature.textColor }}
                >
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 截图瀑布流展示 ===== */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div
          className="absolute top-0 left-0 w-full h-32 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, rgba(10,10,20,1), transparent)' }}
        />
        <div
          className="absolute bottom-0 left-0 w-full h-32 pointer-events-none"
          style={{ background: 'linear-gradient(to top, rgba(10,10,20,1), transparent)' }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">应用截图</h2>
            <p className="text-gray-400">真实界面展示，所见即所得</p>
          </motion.div>

          {/* 两行滚动条幅 */}
          <div className="overflow-hidden space-y-4">
            {/* 第一行：向左滚动 */}
            <motion.div
              className="flex gap-4"
              animate={{ x: [0, -1200] }}
              transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
              style={{ width: 'max-content' }}
            >
              {[...screenshots, ...screenshots].map((s, i) => (
                <div
                  key={i}
                  className="flex-shrink-0 w-36 h-64 rounded-2xl overflow-hidden"
                  style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <img src={s.src} alt={s.label} className="w-full h-full object-cover object-top" />
                </div>
              ))}
            </motion.div>

            {/* 第二行：向右滚动 */}
            <motion.div
              className="flex gap-4"
              animate={{ x: [0, 1200] }}
              transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
              style={{ width: 'max-content' }}
            >
              {[...screenshots.slice(5), ...screenshots, ...screenshots.slice(0, 5)].map((s, i) => (
                <div
                  key={i}
                  className="flex-shrink-0 w-36 h-64 rounded-2xl overflow-hidden"
                  style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <img src={s.src} alt={s.label} className="w-full h-full object-cover object-top" />
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-24 relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.1), rgba(239,68,68,0.08))' }}
        />
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="text-5xl mb-6">🏆</div>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-6">
              接受挑战，证明实力
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto text-lg">
              加入全球华人网球圈，与高手同台竞技
              <br />
              每一分都代表你的实力
            </p>

            {/* 小程序码 */}
            <div className="flex justify-center gap-10 items-start flex-wrap mb-10">
              <div className="flex flex-col items-center gap-3">
                <div className="w-40 h-40 rounded-2xl overflow-hidden shadow-2xl"
                  style={{ border: '3px solid rgba(245,158,11,0.4)', boxShadow: '0 0 40px rgba(245,158,11,0.3)' }}>
                  <img
                    src="/images/ladder/qr.jpg"
                    alt="Lafit网球挑战榜 小程序码"
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-sm text-gray-400">扫码挑战</span>
              </div>
            </div>

            <Link
              to="/#download"
              className="inline-flex items-center gap-2 px-10 py-4 rounded-xl font-bold transition-all duration-300 hover:gap-4 hover:scale-105"
              style={{
                background: '#f59e0b',
                boxShadow: '0 0 40px rgba(245,158,11,0.5)',
                color: '#000',
                fontSize: '1.1rem',
              }}
            >
              立即参与挑战
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
